import '@fortawesome/fontawesome-free/css/all.css';
import {useMediaQuery} from '@material-ui/core';
import AgencyAdministrationRoutes from 'AgencyAdministration/Routes';
import AgentRoutes from 'Agent/Routes';
import AgentNewsAdministrationRoutes from 'AgentNewsAdministration/Routes';
import ApplicationEntryRoutes from 'ApplicationEntry/Routes';
import AuthorizedRoutes from 'AuthorizedRoutes';
import BeneficiariesRoutes from 'Beneficiaries/Routes';
import BOSToolsRoute from 'BOSTools/Routes';
import Authorize from 'Core/components/Authorize';
import Login from 'Core/components/Login/Login';
import CollapsedMenuBar from 'Core/components/Navigation/CollapsedMenuBar';
import NavigationMenu from 'Core/components/Navigation/NavigationMenu';
import Notifications from 'Core/components/Notifications';
import {loadClaimsAndProfile} from 'Core/components/Startup';
import LegalDocumentationRoutes from 'Core/components/TermsAndConditions/Routes';
import TermsAndConditions from 'Core/components/TermsAndConditions/TermsAndConditions';
import UserAccountNotFound from 'Core/components/UserAccountNotFound';
import WithTheme from 'Core/components/WithTheme';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {Empty, fromResult, isLoaded} from 'Core/models/Async';
import CoreRoutes from 'Core/Routes';
import EnrollmentRoutes from 'Enrollment/Routes';
import MessagingRoutes from 'GenericTexting/Routes';
import {sequenceT} from 'fp-ts/lib/Apply';
import * as IO from 'fp-ts/lib/IO';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Route, Switch, useHistory, useLocation} from 'react-router-dom';
import BreadcrumbsBar from 'Shared/components/BreadcrumbsBar';
import ErrorBoundary from 'Shared/components/ErrorBoundary';
import ModalConductor from 'Shared/components/ModalConductor';
import {logout} from 'Shared/utilities/Auth';
import {NoOp} from 'Shared/utilities/IOUtilities';
import {startRefreshTimer} from 'Shared/utilities/RefreshTokenTimer';
import {getUserClaims} from 'Shared/utilities/UserProfileUtilities';
import SystemAdministrationRoutes from 'SystemAdministration/Routes';
import IndependentAgentEntry from 'Users/components/AgentAdministration/AgencyAdministratorAgentEntry/IndependentAgentEntry';
import IndependentAgentRegistration from 'Users/components/IndependentAgentRegistration/IndependentAgentRegistration';
import IndependentAgentSignUp from 'Users/components/SignUp/ExternalUserSignUp';
import PasswordRecovery from 'Users/components/UserRecovery/PasswordReset/PasswordRecovery';
import PasswordRecoveryEmailSent from 'Users/components/UserRecovery/PasswordReset/PasswordRecoveryEmailSent';
import PasswordReset from 'Users/components/UserRecovery/PasswordReset/PasswordReset';
import PasswordResetExpired from 'Users/components/UserRecovery/PasswordReset/PasswordResetExpired';
import UsernameRecovery from 'Users/components/UserRecovery/UsernameRecovery';
import {setUserClaims} from 'Users/ducks/userProfile';
import UserRoutes, {UserRouteConstants} from 'Users/Routes';
import WithClaims from 'WithClaims';
import './App.sass';
import {showModal, ModalState} from 'Core/ducks/modal';
import {ReducerState} from 'rootReducer';
import FileUploadProgressIndicator from 'Core/components/Navigation/FileUploadProgressIndicator';
import {clearSnackbar} from 'Core/ducks/snackbar';
import CustomerSupportRoutes from 'CustomerSupport/Routes';

function inIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

export const App = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();
  const isMobileView = !useMediaQuery((theme: any) => theme.breakpoints.up('lg'));
  const logUserOut = sequenceT(IO.io)(
    logout,
    () => dispatch(clearSnackbar()),
    () => dispatch(setUserClaims(new Empty())),
    () => history.push(CoreRouteConstants.LOGIN)
  );
  const Menu = CollapsedMenuBar;
  const handleTokenRefreshTimer = inIframe() ? NoOp : () => startRefreshTimer(dispatch);
  const initApplication = sequenceT(IO.io)(handleTokenRefreshTimer);

  const {userClaims} = useSelector((state: ReducerState) => state.user.profile);

  useEffect(() => {
    initApplication();

    pipe(
      O.some(loadClaimsAndProfile(dispatch)),
      O.ap(getUserClaims()),
      O.fold(
        () => {},
        load => load()
      )
    );
  }, []);

  useEffect(() => {
    if (isLoaded(userClaims) && userClaims.Data.shouldResetPassword) {
      dispatch(showModal(ModalState.ReviewSecurityQuestionsAndResetPassword(userClaims.Data)));
    }
  }, [userClaims]);

  return (
    <ErrorBoundary>
      <Switch>
        <Route exact path="/authorize" render={routeProps => <Authorize {...routeProps} />} />
        <Route exact path="/login" render={() => <Login />} />
        <Route exact path="/noAccess" render={() => <UserAccountNotFound onLogout={logUserOut} />} />
        <Route
          exact
          path={UserRouteConstants.SIGN_UP}
          render={routeProps => <IndependentAgentEntry {...routeProps} />}
        />
        <Route exact path={UserRouteConstants.USERNAME_RECOVERY} render={() => <UsernameRecovery />} />
        <Route exact path={UserRouteConstants.PASSWORD_RECOVERY} render={() => <PasswordRecovery />} />
        <Route
          exact
          path={UserRouteConstants.PASSWORD_RECOVERY_EMAIL_SENT}
          render={() => <PasswordRecoveryEmailSent />}
        />
        <Route exact path={UserRouteConstants.PASSWORD_RESET_REQUEST_EXPIRED} render={() => <PasswordResetExpired />} />
        <Route exact path={UserRouteConstants.PASSWORD_RESET_FORM(':requestKey')} render={() => <PasswordReset />} />
        <Route exact path={UserRouteConstants.REGISTRATION} render={() => <IndependentAgentRegistration />} />
        <Route
          exact
          path={UserRouteConstants.EXTERNAL_USER_SIGN_UP(':preregistrationKey')}
          render={() => <IndependentAgentSignUp />}
        />
        <AuthorizedRoutes>
          <WithClaims>
            {claims => (
              <TermsAndConditions claims={claims}>
                <Menu onLogout={logUserOut} userClaims={fromResult(claims)} />
                {!isMobileView && <NavigationMenu userClaims={fromResult(claims)} />}
                <div className="app">
                  {!isMobileView && <BreadcrumbsBar />}
                  <AgentNewsAdministrationRoutes />
                  <AgencyAdministrationRoutes />
                  <CustomerSupportRoutes />
                  <UserRoutes />
                  <ApplicationEntryRoutes />
                  <EnrollmentRoutes />
                  <LegalDocumentationRoutes url={location.pathname} />
                  <BeneficiariesRoutes />
                  <BOSToolsRoute />
                  <CoreRoutes claims={claims} />
                  <AgentRoutes claims={claims} />
                  <SystemAdministrationRoutes />
                  <MessagingRoutes />
                </div>
              </TermsAndConditions>
            )}
          </WithClaims>
        </AuthorizedRoutes>
      </Switch>
      <ModalConductor />
      <Notifications />
      <FileUploadProgressIndicator />
    </ErrorBoundary>
  );
};

export default WithTheme(App);
